import { Bar } from 'react-chartjs-2';
import { useEffect, useState } from 'react';
import { fetchAPI } from '../../utils/utils';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import treasuryChartResize from './treasuryChartResize';
import { formatMoney } from '../../utils/money';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

function QuoteRevenueChart({ quotes = [], allQuotePayments = {}, subscriptionStats = {}, castellersInfo = {} }) {
    const [dataPoints, setDataPoints] = useState([]);

    useEffect(() => {
        fetchAPI('/quotes/revenue', data => setDataPoints(data || []));
    }, []);

    // Helper function to calculate pending amount for a quote
    const calculatePendingAmount = (quoteId) => {
        const payments = allQuotePayments[quoteId] || [];
        const pendingPayments = payments.filter(p => !p.pagada && !p.exempt && p.payment_reference);
        
        // Find the quote to get its base value (fallback)
        const quote = quotes.find(q => q.id === quoteId);
        const quoteValue = quote ? parseFloat(quote.value || 0) : 0;
        
        // Calculate actual pending amount using variant data if available
        return pendingPayments.reduce((sum, p) => {
            // If payment has a variant, use variant import + children addon
            if (p.variant_import !== null && p.variant_import !== undefined) {
                const variantAmount = parseFloat(p.variant_import || 0);
                const childrenAddon = parseFloat(p.num_children || 0) * parseFloat(p.children_price || 0);
                return sum + variantAmount + childrenAddon;
            }
            // Otherwise use quote base value
            return sum + quoteValue;
        }, 0);
    };

    if (dataPoints.length === 0) {
        return null;
    }

    // Reverse the data so newest is on the right
    const reversedData = [...dataPoints].reverse();
    
    // Create better labels
    const monthNames = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 
                       'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];
    
    const labels = reversedData.map(q => {
        if (q.titol) return q.titol;
        if (q.tipus === 'anual') {
            return q.any.toString();
        } else if (q.tipus === 'mensual') {
            return `${monthNames[q.mes - 1]} ${q.any}`;
        } else if (q.tipus === 'trimestral') {
            const trimestre = Math.ceil(q.mes / 3);
            return `T${trimestre} ${q.any}`;
        } else if (q.tipus === 'quadrimestral') {
            const quadrimestre = Math.ceil(q.mes / 4);
            return `Q${quadrimestre} ${q.any}`;
        }
        return `${q.mes}/${q.any}`;
    });
    
    const revenues = reversedData.map(q => {
        const baseRevenue = parseFloat(q.revenue || 0);
        // Add subscription revenue for subscription quotes (use actual revenue, fallback to projected)
        const subStats = subscriptionStats[q.id];
        const subRevenue = subStats ? parseFloat(subStats.revenue || subStats.projected_revenue || 0) : 0;
        return baseRevenue + subRevenue;
    });
    const donations = reversedData.map(q => {
        const baseDonations = parseFloat(q.donations || 0);
        // Add subscription donations for subscription quotes
        const subStats = subscriptionStats[q.id];
        const subDonations = subStats ? parseFloat(subStats.donations || 0) : 0;
        return baseDonations + subDonations;
    });
    const pending = reversedData.map(q => calculatePendingAmount(q.id));

    // Check if there are any donations > 0
    const hasDonations = donations.some(d => d > 0);

    // Build datasets conditionally
    const datasets = [
        {
            label: 'Quotes',
            data: revenues,
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderColor: 'rgba(99, 102, 241, 1)',
            borderWidth: 2,
            borderRadius: 4,
            maxBarThickness: 120,
            datalabels: {
                display: false // Hide individual labels for cleaner look
            }
        }
    ];

    // Only add donations dataset if there are donations
    if (hasDonations) {
        datasets.push({
            label: 'Donatius',
            data: donations,
            backgroundColor: 'rgba(236, 72, 153, 0.8)',
            borderColor: 'rgba(236, 72, 153, 1)',
            borderWidth: 2,
            borderRadius: 4,
            maxBarThickness: 120,
            datalabels: {
                display: false // Hide individual labels for cleaner look
            }
        });
    }

    // Add pending dataset
    datasets.push({
        label: 'Pendents',
        data: pending,
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 2,
        borderRadius: 4,
        maxBarThickness: 120,
        datalabels: {
            display: false // Hide individual labels for cleaner look
        }
    });

    const data = {
        labels,
        datasets
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onResize: treasuryChartResize,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { 
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '500'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(45, 55, 72, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    title: (contexts) => {
                        const context = Array.isArray(contexts) ? contexts[0] : contexts;
                        const item = reversedData[context.dataIndex];
                        return item.titol || `${labels[context.dataIndex]} · ${item.tipus}`;
                    },
                    label: (context) => {
                        const item = reversedData[context.dataIndex];
                        const baseRevenue = parseFloat(item.revenue || 0);
                        const baseDonations = parseFloat(item.donations || 0);
                        // Include subscription revenue/donations (use actual revenue, fallback to projected)
                        const subStats = subscriptionStats[item.id];
                        const subRevenue = subStats ? parseFloat(subStats.revenue || subStats.projected_revenue || 0) : 0;
                        const subDonations = subStats ? parseFloat(subStats.donations || 0) : 0;
                        const totalRevenue = baseRevenue + subRevenue;
                        const totalDonations = baseDonations + subDonations;
                        const pendingAmount = calculatePendingAmount(item.id);
                        const datasetLabel = context.dataset.label;
                        
                        if (datasetLabel === 'Quotes') {
                            return `Quotes: ${formatMoney(totalRevenue)}€`;
                        } else if (datasetLabel === 'Donatius') {
                            return `Donatius: ${formatMoney(totalDonations)}€`;
                        } else if (datasetLabel === 'Pendents') {
                            return `Pendents: ${formatMoney(pendingAmount)}€`;
                        }
                        return '';
                    },
                    afterBody: (contexts) => {
                        const context = Array.isArray(contexts) ? contexts[0] : contexts;
                        const item = reversedData[context.dataIndex];
                        const baseTotal = parseFloat(item.total_revenue || 0);
                        // Include subscription revenue in total (use actual revenue, fallback to projected)
                        const subStats = subscriptionStats[item.id];
                        const subRevenue = subStats ? parseFloat(subStats.revenue || subStats.projected_revenue || 0) : 0;
                        const subDonations = subStats ? parseFloat(subStats.donations || 0) : 0;
                        const total = baseTotal + subRevenue + subDonations;
                        const pendingAmount = calculatePendingAmount(item.id);
                        const grandTotal = total + pendingAmount;
                        return [``, `Confirmat: ${formatMoney(total)}€`, `Total potencial: ${formatMoney(grandTotal)}€`];
                    }
                }
            },
            datalabels: {
                display: function(context) {
                    // Only show total on the top of the stack (last dataset, which is always Pendents)
                    return context.datasetIndex === context.chart.data.datasets.length - 1;
                },
                anchor: 'end',
                align: 'top',
                formatter: (value, context) => {
                    const item = reversedData[context.dataIndex];
                    const baseTotal = parseFloat(item.total_revenue || 0);
                    // Include subscription revenue in total (use actual revenue, fallback to projected)
                    const subStats = subscriptionStats[item.id];
                    const subRevenue = subStats ? parseFloat(subStats.revenue || subStats.projected_revenue || 0) : 0;
                    const subDonations = subStats ? parseFloat(subStats.donations || 0) : 0;
                    const total = baseTotal + subRevenue + subDonations;
                    const pendingAmount = calculatePendingAmount(item.id);
                    const grandTotal = total + pendingAmount;
                    return grandTotal > 0 ? `${grandTotal.toFixed(0)}€` : '';
                },
                color: '#4a5568',
                font: {
                    size: 11,
                    weight: '600'
                },
                padding: 4
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: {
                    color: '#718096',
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    autoSkip: true,
                    maxRotation: 45,
                    minRotation: 0
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: {
                    color: '#e2e8f0',
                    drawBorder: false
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#718096',
                    font: {
                        size: 12
                    },
                    padding: 8,
                    callback: function(value) {
                        return formatMoney(value) + '€';
                    }
                }
            }
        },
        layout: {
            padding: {
                top: 30
            }
        }
    };

    return (
        <div className="quotes-chart-container">
            <h3 className="chart-title">Ingressos per quota: quotes i donatius</h3>
            <div className="quotes-chart">
                <Bar data={data} options={options} />
            </div>
        </div>
    );
}

export default QuoteRevenueChart;
